import React, { useEffect, useMemo, useRef, useState } from 'react';
import './DataTable.css';

const CompactIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 5h18" />
    <path d="M3 12h18" />
    <path d="M3 19h18" />
  </svg>
);

const DetailedIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 5h3" />
    <path d="M10 5h10" />
    <path d="M4 12h3" />
    <path d="M10 12h10" />
    <path d="M4 19h3" />
    <path d="M10 19h10" />
  </svg>
);

const CardsIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="3" width="7" height="7" rx="1.4" />
    <rect x="14" y="3" width="7" height="7" rx="1.4" />
    <rect x="3" y="14" width="7" height="7" rx="1.4" />
    <rect x="14" y="14" width="7" height="7" rx="1.4" />
  </svg>
);

const ChevronIcon = ({ isOpen }) => (
  <svg
    className={`database-view-menu__chevron ${isOpen ? 'is-open' : ''}`}
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const VIEW_OPTIONS = [
  { value: 'compact', label: 'Compact', Icon: CompactIcon },
  { value: 'detailed', label: 'Detailed', Icon: DetailedIcon },
  { value: 'card', label: 'Cards', Icon: CardsIcon }
];

function normalizeSelection(value) {
  return value === 'compact' || value === 'card' ? value : 'detailed';
}

const DatabaseViewMenu = ({ selected = 'detailed', onSelect }) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const normalizedSelection = useMemo(() => normalizeSelection(selected), [selected]);
  const activeOption = VIEW_OPTIONS.find((option) => option.value === normalizedSelection) || VIEW_OPTIONS[1];

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handlePointerDown = (event) => {
      if (!menuRef.current || !triggerRef.current) {
        return;
      }
      if (!menuRef.current.contains(event.target) && !triggerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open]);

  const handleSelect = (value) => {
    if (typeof onSelect === 'function') {
      onSelect(value);
    }
    setOpen(false);
  };

  return (
    <div className={`database-view-menu ${open ? 'is-open' : ''}`}>
      <button
        type="button"
        className="database-view-menu__trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        title={activeOption ? `Vue ${activeOption.label}` : 'Changer de vue'}
        aria-label={activeOption ? `Vue ${activeOption.label}` : 'Changer de vue'}
        onClick={() => setOpen((prev) => !prev)}
        ref={triggerRef}
      >
        <span className="database-view-menu__trigger-icon">
          {activeOption && <activeOption.Icon />}
        </span>
        <ChevronIcon isOpen={open} />
      </button>
      <div
        className="database-view-menu__options"
        role="menu"
        ref={menuRef}
        hidden={!open}
      >
        {VIEW_OPTIONS.map(({ value, label, Icon }) => {
          const isActive = normalizedSelection === value;
          return (
            <button
              key={value}
              type="button"
              role="menuitemradio"
              aria-checked={isActive}
              className={`database-view-menu__option ${isActive ? 'is-active' : ''}`}
              onClick={() => handleSelect(value)}
            >
              <Icon />
              <span>{label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default DatabaseViewMenu;
